import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentIcon,
  Cog6ToothIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DoublesAnalysisTables, mergeExpandedRows } from "../components/DoublesAnalysisSection";
import { useAuthStatus } from "../hooks/useAuthStatus";
import { fetchMatchWithData } from "../utils/dbutils";
import { computeDoublesAnalysis, expandRow } from "../utils/doublesAnalysis";
import { updateStats } from "../utils/StatsObject";

// ── Constants ─────────────────────────────────────────────────────────────────

const OPENAI_KEY_LS = "psts_openai_key";
const ANTHROPIC_KEY_LS = "psts_anthropic_key";
const OPENAI_MODEL_LS = "psts_openai_model";

const MODELS = [
  { id: "gpt-4o", label: "GPT-4o", provider: "openai" },
  { id: "gpt-4o-mini", label: "GPT-4o Mini (faster, cheaper)", provider: "openai" },
  { id: "gpt-4-turbo", label: "GPT-4 Turbo", provider: "openai" },
  { id: "gpt-3.5-turbo", label: "GPT-3.5 Turbo", provider: "openai" },
  { id: "claude-opus-4-20250514", label: "Claude Opus 4", provider: "anthropic" },
  { id: "claude-sonnet-4-20250514", label: "Claude Sonnet 4", provider: "anthropic" },
  { id: "claude-3-5-sonnet-20241022", label: "Claude 3.5 Sonnet", provider: "anthropic" },
  { id: "claude-3-5-haiku-20241022", label: "Claude 3.5 Haiku (faster, cheaper)", provider: "anthropic" },
];

const providerOf = (modelId) => {
  const known = MODELS.find((m) => m.id === modelId)?.provider;
  if (known) return known;
  return /^claude/i.test(modelId ?? "") ? "anthropic" : "openai";
};

// Turn low-level fetch/network failures into an actionable message.
const describeError = (err, providerName) => {
  const msg = err?.message ?? "";
  if (err?.name === "TypeError" || /failed to fetch|load failed|networkerror/i.test(msg)) {
    const host = providerName === "Anthropic" ? "api.anthropic.com" : "api.openai.com";
    return `Couldn't reach ${providerName}. The browser blocked or failed the request to ${host} — this is usually an ad blocker / privacy extension, a VPN or firewall, or being offline. Allow ${host} and try again.`;
  }
  return msg || "Unknown error";
};

const SYSTEM_PROMPT = `You are an elite tennis coach and performance analyst. Based on match statistics provided, write a detailed coaching report structured as follows:

## Executive Summary
A concise 2-3 sentence overview of the player's overall performance.

## Key Strengths
Bullet points identifying what the player does well, with specific references to their stats.

## Areas for Improvement
Bullet points identifying weaknesses relative to their opponent(s), with specific stat references.

## Serving Analysis
Detailed analysis of serve efficiency, aggression, and reliability. Reference all serve stats.

## Return Game Analysis
Analysis of the player's ability to win points off the opponent's serve. Reference all return stats.

## Rally Patterns & Tactical Profile
Analysis of short/medium/long rally performance, positioning, and implied tactical style.

## Recommendations
3-5 specific, actionable training and tactical recommendations based on the data.

Write in a professional coaching tone. Reference actual numbers throughout. Be precise and insightful.`;

const OPPOSITION_PROMPT = `You are an elite tennis coach preparing your player to face a specific opponent. Based on that opponent's match statistics provided, create a detailed tactical game plan structured as follows:

## Scouting Summary
A concise 2-3 sentence overview of the opponent's playing style and key characteristics.

## Key Threats to Neutralise
Bullet points identifying the opponent's strengths that must be managed, with specific stat references.

## Vulnerabilities to Exploit
Bullet points identifying the opponent's weaknesses and how to target them, with specific stat references.

## Serving Strategy
How to serve against this opponent — patterns, placement, and tactics based on their return stats.

## Return Strategy
How to return against this opponent's serve — what to expect and how to take control, based on their serve stats.

## Rally & Tactical Approach
How to construct points against this player — ideal rally length targets, court positioning, and patterns of play.

## Match Plan
3-5 specific, actionable tactical instructions prioritised by importance.

Write in a professional coaching tone as if briefing a player before a match. Reference actual numbers throughout. Be precise and tactically specific.`;

const DOUBLES_WEAKEST_LINK_PROMPT = `You are an elite doubles coach analysing a team's match statistics, both combined and broken down per individual player. Your job is to identify the team's "weakest link" — the partner whose statistics most hold the team back — and how the team should adapt around it. Structure your report as follows:

## Team Summary
A concise 2-3 sentence overview of the team's combined performance versus their opponent(s).

## Player Comparison
Present a markdown table comparing the two partners' individual stats — one row per stat (serving, returning, errors, pressure points), with a column for each player. Use exact numbers from the data provided. Format it as a standard markdown table, e.g.:

| Stat | Player A | Player B |
| --- | --- | --- |
| 1st Serve % | 62% | 58% |

## Identified Weakest Link
State which of the two players is statistically the weaker link right now, and precisely which stats justify that conclusion. If the data doesn't clearly point to one player, say so and explain why (e.g. both contribute comparable weaknesses in different areas).

## Impact on the Team
Explain how the weaker player's specific gaps are affecting the team's results (points lost, break points conceded, etc.), with numbers.

## Tactical Adjustments
3-5 specific, actionable recommendations — e.g. formation changes, who should take more balls down the middle, serve/return positioning, or targeted practice — to cover for the weaker link and maximise the team's combined strength.

Write in a professional coaching tone. Be direct but constructive — the goal is genuinely improving the team, not criticising a player. Reference actual numbers throughout.`;

// ── Helpers ───────────────────────────────────────────────────────────────────

const pct = (a, b) => (b == null || b === 0 ? "N/A" : `${((a * 100) / b).toFixed(0)}%`);

// Same mergeStats as StatsPage
const mergeStats = (a, b) => {
  if (!a) return { ...b };
  if (!b) return { ...a };
  const result = { ...a };
  for (const key of Object.keys(b)) {
    if (Array.isArray(b[key])) {
      result[key] = [...(a[key] ?? []), ...b[key]];
    } else if (typeof b[key] === "number") {
      result[key] = (a[key] ?? 0) + b[key];
    }
  }
  return result;
};

function buildStatsText(sA, sB, playerName, opponentLabel) {
  const ue1 = sA.forehandUnforcedErrors + sA.backhandUnforcedErrors;
  const ue2 = sB.forehandUnforcedErrors + sB.backhandUnforcedErrors;
  const fe1 = sA.forehandForcingErrors + sA.backhandForcingErrors;
  const fe2 = sB.forehandForcingErrors + sB.backhandForcingErrors;

  return [
    `Player under analysis: ${playerName}`,
    `Opponent(s): ${opponentLabel}`,
    ``,
    `=== POINTS ===`,
    `Pressure Points Won:       ${sA.totalPressurePointsWon} vs ${sB.totalPressurePointsWon}`,
    `Winners:                   ${sA.totalWinners} vs ${sB.totalWinners}`,
    `Unforced Errors:           ${ue1} vs ${ue2}`,
    `Forced Errors (opponent):  ${fe1} vs ${fe2}`,
    ``,
    `=== SERVING ===`,
    `Aces + Serve Winners:      ${sA.aces + sA.serveWinners} vs ${sB.aces + sB.serveWinners}`,
    `Double Faults:             ${sA.doublefaults} vs ${sB.doublefaults}`,
    `1st Serve %:               ${pct(sA.firstServeIns, sA.firstServes)} vs ${pct(sB.firstServeIns, sB.firstServes)}`,
    `1st Serve Points Won:      ${pct(sA.firstServicePointsWon, sA.firstServeIns)} vs ${pct(sB.firstServicePointsWon, sB.firstServeIns)}`,
    `2nd Serve Points Won:      ${pct(sA.secondServicePointsWon, sA.secondServeIns)} vs ${pct(sB.secondServicePointsWon, sB.secondServeIns)}`,
    ``,
    `=== RETURNING ===`,
    `1st Serve Return Won:      ${pct(sA.pointsWonOnReturnOffFirstServes, sA.returnsFirstServe)} vs ${pct(sB.pointsWonOnReturnOffFirstServes, sB.returnsFirstServe)}`,
    `2nd Serve Return Won:      ${pct(sA.pointsWonOnReturnOffSecondServes, sA.returnsSecondServe)} vs ${pct(sB.pointsWonOnReturnOffSecondServes, sB.returnsSecondServe)}`,
    ``,
    `=== BREAK POINTS ===`,
    `Break Points Won:          ${sA.breakPointsConverted}/${sA.breakPoints} (${pct(sA.breakPointsConverted, sA.breakPoints)}) vs ${sB.breakPointsConverted}/${sB.breakPoints} (${pct(sB.breakPointsConverted, sB.breakPoints)})`,
    ``,
    `=== RALLY LENGTHS ===`,
    `Short Rallies (0-4) Won:   ${sA.totalShortRalliesWon} vs ${sB.totalShortRalliesWon}`,
    `Medium Rallies Won:        ${sA.totalMediumRalliesWon} vs ${sB.totalMediumRalliesWon}`,
    `Long Rallies Won:          ${sA.totalLongRalliesWon} vs ${sB.totalLongRalliesWon}`,
    ``,
    `=== POSITIONING ===`,
    `Shots Within 2m Baseline:  ${sA.totalShotsWithin2mOfBaseLine} vs ${sB.totalShotsWithin2mOfBaseLine}`,
  ].join("\n");
}

function buildWeakestLinkStatsText(teamStats, oppStats, p1Stats, p1Name, p2Stats, p2Name, teamName, opponentLabel) {
  const row = (label, s1, s2) =>
    `${label.padEnd(28)}${String(s1).padEnd(22)}${s2}`;
  const ue = (s) => s.forehandUnforcedErrors + s.backhandUnforcedErrors;

  return [
    `Team under analysis: ${teamName} (players: ${p1Name} & ${p2Name})`,
    `Opponent(s): ${opponentLabel}`,
    ``,
    `=== TEAM TOTALS (${teamName} vs ${opponentLabel}) ===`,
    row("Pressure Points Won:", teamStats.totalPressurePointsWon, oppStats.totalPressurePointsWon),
    row("Winners:", teamStats.totalWinners, oppStats.totalWinners),
    row("Unforced Errors:", ue(teamStats), ue(oppStats)),
    row("1st Serve %:", pct(teamStats.firstServeIns, teamStats.firstServes), pct(oppStats.firstServeIns, oppStats.firstServes)),
    row("1st Serve Points Won:", pct(teamStats.firstServicePointsWon, teamStats.firstServeIns), pct(oppStats.firstServicePointsWon, oppStats.firstServeIns)),
    row("2nd Serve Points Won:", pct(teamStats.secondServicePointsWon, teamStats.secondServeIns), pct(oppStats.secondServicePointsWon, oppStats.secondServeIns)),
    row("1st Serve Return Won:", pct(teamStats.pointsWonOnReturnOffFirstServes, teamStats.returnsFirstServe), pct(oppStats.pointsWonOnReturnOffFirstServes, oppStats.returnsFirstServe)),
    row("2nd Serve Return Won:", pct(teamStats.pointsWonOnReturnOffSecondServes, teamStats.returnsSecondServe), pct(oppStats.pointsWonOnReturnOffSecondServes, oppStats.returnsSecondServe)),
    row("Break Points Won:", `${teamStats.breakPointsConverted}/${teamStats.breakPoints}`, `${oppStats.breakPointsConverted}/${oppStats.breakPoints}`),
    ``,
    `=== INDIVIDUAL COMPARISON (${p1Name} vs ${p2Name}) ===`,
    `Note: rally-level team stats (Total Points Won, Break Points Won, Rally Length) are identical for both partners — they are shared team outcomes, not attributable to one player. Only per-shot stats below distinguish the two.`,
    row("Winners:", p1Stats.totalWinners ?? 0, p2Stats.totalWinners ?? 0),
    row("Unforced Errors:", ue(p1Stats), ue(p2Stats)),
    row("Aces + Serve Winners:", (p1Stats.aces ?? 0) + (p1Stats.serveWinners ?? 0), (p2Stats.aces ?? 0) + (p2Stats.serveWinners ?? 0)),
    row("Double Faults:", p1Stats.doublefaults ?? 0, p2Stats.doublefaults ?? 0),
    row("1st Serve %:", pct(p1Stats.firstServeIns, p1Stats.firstServes), pct(p2Stats.firstServeIns, p2Stats.firstServes)),
    row("1st Serve Points Won:", pct(p1Stats.firstServicePointsWon, p1Stats.firstServeIns), pct(p2Stats.firstServicePointsWon, p2Stats.firstServeIns)),
    row("2nd Serve Points Won:", pct(p1Stats.secondServicePointsWon, p1Stats.secondServeIns), pct(p2Stats.secondServicePointsWon, p2Stats.secondServeIns)),
    row("1st Serve Return Won:", pct(p1Stats.pointsWonOnReturnOffFirstServes, p1Stats.returnsFirstServe), pct(p2Stats.pointsWonOnReturnOffFirstServes, p2Stats.returnsFirstServe)),
    row("2nd Serve Return Won:", pct(p1Stats.pointsWonOnReturnOffSecondServes, p1Stats.returnsSecondServe), pct(p2Stats.pointsWonOnReturnOffSecondServes, p2Stats.returnsSecondServe)),
  ].join("\n");
}

// Flattens each Doubles Analysis category's "Player + 1st/2nd" breakdown
// (see DoublesAnalysisSection.jsx) into {section}/{group}/{label, v1, v2}
// rows for the PDF's plain text/table renderer — Net Position keeps its
// original combined totals since it has no per-player/serve# breakdown.
function buildDoublesAnalysisRows(analysis) {
  if (!analysis) return [];
  const rows = [];
  const wonStr = (won, points) => (points > 0 ? `${won}/${points} (${pct(won, points)})` : "\u2014");
  // A leaf's own faults (only non-zero for serve direction) reduce the
  // meaningful denominator to just its in-play attempts.
  const itemStr = (item) => (item?.points ? wonStr(item.won, item.points - (item.faults ?? 0)) : "\u2014");

  const addBrokenDownSection = (title, focusRows, oppRows) => {
    const visible = focusRows
      .map((row, i) => ({ row, oppRow: oppRows?.[i] }))
      .filter(({ row, oppRow }) => row.total.points > 0 || (oppRow?.total.points ?? 0) > 0);
    if (visible.length === 0) return;
    rows.push({ section: title });
    for (const { row, oppRow } of visible) {
      rows.push({ group: row.label });
      const sub = mergeExpandedRows(
        expandRow(row, "playerServe"),
        oppRow ? expandRow(oppRow, "playerServe") : null,
      );
      for (const s of sub) {
        rows.push({ label: s.label, v1: itemStr(s.focus), v2: itemStr(s.opp) });
      }
    }
  };

  addBrokenDownSection("Serve Formations", analysis.focus.serveFormations, analysis.opposition?.serveFormations);
  addBrokenDownSection("Serve Direction (won/in-play)", analysis.focus.serveDirection, analysis.opposition?.serveDirection);

  const netRows = analysis.focus.netPositions;
  const netOppRows = analysis.opposition?.netPositions;
  const visibleNet = netRows.filter((r, i) => r.points > 0 || (netOppRows?.[i]?.points ?? 0) > 0);
  if (visibleNet.length > 0) {
    rows.push({ section: "Net Position (on serve)" });
    for (const r of visibleNet) {
      const oppR = netOppRows?.[netRows.indexOf(r)];
      rows.push({
        label: r.label,
        v1: wonStr(r.won, r.points),
        v2: oppR ? wonStr(oppR.won, oppR.points) : "\u2014",
      });
    }
  }

  addBrokenDownSection("Return Formations", analysis.focus.returnFormations, analysis.opposition?.returnFormations);

  const handFocusRows = [analysis.focus.returnHand.forehand, analysis.focus.returnHand.backhand];
  const handOppRows = analysis.opposition ? [analysis.opposition.returnHand.forehand, analysis.opposition.returnHand.backhand] : null;
  addBrokenDownSection("Return Success by Hand", handFocusRows, handOppRows);

  return rows;
}


// ── Simple Markdown renderer ──────────────────────────────────────────────────

function renderInline(text, keyPrefix) {
  const parts = text.split(/(\*\*[^*]+\*\*)/);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={`${keyPrefix}-${i}`}>{p.slice(2, -2)}</strong>
    ) : (
      p
    )
  );
}

// A GFM table separator row, e.g. "| --- | :--- | ---: |".
function isTableSeparatorRow(line) {
  return /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?$/.test(line.trim());
}

function parseTableRow(line) {
  return line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
}

function MarkdownReport({ text }) {
  if (!text) return null;
  const elements = [];
  const lines = text.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim().startsWith("|") && isTableSeparatorRow(lines[i + 1] ?? "")) {
      const header = parseTableRow(line);
      const bodyRows = [];
      let j = i + 2;
      while (j < lines.length && lines[j].trim().startsWith("|")) {
        bodyRows.push(parseTableRow(lines[j]));
        j++;
      }
      elements.push(
        <div key={i} className="overflow-x-auto my-2">
          <table className="table table-xs w-full">
            <thead>
              <tr>
                {header.map((h, hi) => (
                  <th key={hi} className={hi === 0 ? "text-left" : "text-right"}>{renderInline(h, `${i}-h-${hi}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((r, ri) => (
                <tr key={ri} className="hover">
                  {r.map((c, ci) => (
                    <td key={ci} className={ci === 0 ? "" : "text-right"}>{renderInline(c, `${i}-${ri}-${ci}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      i = j - 1;
      continue;
    }
    if (line.startsWith("## ")) {
      elements.push(
        <h2 key={i} className="text-base font-bold mt-6 mb-2 text-primary border-b border-primary/20 pb-1">
          {renderInline(line.slice(3), i)}
        </h2>
      );
    } else if (line.startsWith("### ")) {
      elements.push(
        <h3 key={i} className="text-sm font-semibold mt-4 mb-1">
          {renderInline(line.slice(4), i)}
        </h3>
      );
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      elements.push(
        <div key={i} className="flex gap-2 ml-1 my-0.5">
          <span className="shrink-0 text-primary mt-[3px] text-xs select-none">•</span>
          <p className="text-sm leading-relaxed">{renderInline(line.slice(2), i)}</p>
        </div>
      );
    } else if (!line.trim()) {
      elements.push(<div key={i} className="h-1.5" />);
    } else {
      elements.push(
        <p key={i} className="text-sm leading-relaxed">
          {renderInline(line, i)}
        </p>
      );
    }
  }
  return <div className="flex flex-col">{elements}</div>;
}

// ── AIReports ─────────────────────────────────────────────────────────────────

function AIReports() {
  const { state } = useLocation();
  const navigate = useNavigate();

  const matchGuids = state?.matchGuids ?? (state?.match?.guid ? [state.match.guid] : []);
  const playerGuid = state?.playerGuid ?? state?.match?.player1Guid ?? null;

  const { firebaseUser } = useAuthStatus();
  const uid = firebaseUser?.uid ?? null;

  // Stats
  const [matches, setMatches] = useState([]);
  const [mergedStatsA, setMergedStatsA] = useState(null);
  const [mergedStatsB, setMergedStatsB] = useState(null);
  // Doubles-only: the focus team's 2 individuals' own stats, for the
  // "Weakest Link" stream.
  const [mergedFocusP1Stats, setMergedFocusP1Stats] = useState(null);
  const [mergedFocusP2Stats, setMergedFocusP2Stats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [showStats, setShowStats] = useState(false);
  // Doubles-only: formation/serve-direction/return-hand breakdown for the
  // "Doubles Analysis" collapsible below, defaulting to the most detailed
  // view (Player + 1st/2nd) since this is a coaching-analysis page.
  const [showDoublesAnalysis, setShowDoublesAnalysis] = useState(false);
  const [serveFormationMode, setServeFormationMode] = useState("playerServe");
  const [serveDirectionMode, setServeDirectionMode] = useState("playerServe");
  const [returnFormationMode, setReturnFormationMode] = useState("playerServe");
  const [returnHandMode, setReturnHandMode] = useState("playerServe");

  // Report
  const [report, setReport] = useState("");
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState("");
  const [savingPdf, setSavingPdf] = useState(false);
  const abortRef = useRef(null);
  const reportEndRef = useRef(null);
  const reportRef = useRef(null);

  // Stream
  const [stream, setStream] = useState("self"); // "self" | "opposition"

  // Settings
  const [openaiKey, setOpenaiKey] = useState(() => localStorage.getItem(OPENAI_KEY_LS) ?? "");
  const [anthropicKey, setAnthropicKey] = useState(() => localStorage.getItem(ANTHROPIC_KEY_LS) ?? "");
  const [model, setModel] = useState(() => localStorage.getItem(OPENAI_MODEL_LS) ?? "gpt-4o-mini");
  const provider = providerOf(model);
  const apiKey = provider === "anthropic" ? anthropicKey : openaiKey;
  const [showSettings, setShowSettings] = useState(false);
  const [openaiKeyDraft, setOpenaiKeyDraft] = useState("");
  const [anthropicKeyDraft, setAnthropicKeyDraft] = useState("");
  const [modelDraft, setModelDraft] = useState("gpt-4o-mini");
  const [fetchedModels, setFetchedModels] = useState(null);
  const [loadingModels, setLoadingModels] = useState(false);
  const [modelsError, setModelsError] = useState("");

  // Cleanup on unmount
  useEffect(() => () => abortRef.current?.abort(), []);

  // Clear report when stream changes
  useEffect(() => {
    abortRef.current?.abort();
    setReport("");
    setGenError("");
  }, [stream]);

  // Auto-scroll report while streaming
  useEffect(() => {
    if (generating) reportEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [report, generating]);

  // Doubles-only: which team of `m` is the "focus" team for playerGuid —
  // same fallback-guid logic used throughout the app (team.guid is often
  // unset for ad-hoc pairings, so individual player guids are the fallback).
  const isFocusTeamA = (m, guid) => {
    if (!guid) return true;
    const teamAGuid =
      m.teamA?.guid ||
      (m.teamA?.player1?.guid && m.teamA?.player2?.guid
        ? `${m.teamA.player1.guid}_${m.teamA.player2.guid}`
        : null);
    return (
      teamAGuid === guid ||
      m.teamA?.player1?.guid === guid ||
      m.teamA?.player2?.guid === guid
    );
  };

  // Per-individual stats for one doubles player, narrowed via updateStats'
  // focusIndividualGuid mechanism (see StatsObject.js) — same technique as
  // GeneralReport.jsx's individualStatsForPlayer.
  const individualStatsForPlayer = (m, individual) => {
    if (!individual) return null;
    return updateStats(m, 0, individual.guid).statsA;
  };

  // ── Load matches & compute stats ─────────────────────────────────────────────
  useEffect(() => {
    if (!matchGuids.length || !uid) return;
    setLoadingStats(true);
    (async () => {
      const fetched = (
        await Promise.all(matchGuids.map((g) => fetchMatchWithData(uid, g)))
      ).filter(Boolean);
      setMatches(fetched);

      let sA = null;
      let sB = null;
      let fp1 = null;
      let fp2 = null;
      for (const m of fetched) {
        const ret = updateStats(m, 0);
        const focusIsP1 = m.isDoubles ? isFocusTeamA(m, playerGuid) : (!playerGuid || m.player1Guid === playerGuid);
        sA = mergeStats(sA, focusIsP1 ? ret.statsA : ret.statsB);
        sB = mergeStats(sB, focusIsP1 ? ret.statsB : ret.statsA);

        if (m.isDoubles) {
          const focusTeam = focusIsP1 ? m.teamA : m.teamB;
          if (focusTeam?.player1) fp1 = mergeStats(fp1, individualStatsForPlayer(m, focusTeam.player1));
          if (focusTeam?.player2) fp2 = mergeStats(fp2, individualStatsForPlayer(m, focusTeam.player2));
        }
      }
      setMergedStatsA(sA);
      setMergedStatsB(sB);
      setMergedFocusP1Stats(fp1);
      setMergedFocusP2Stats(fp2);
      setLoadingStats(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchGuids.join(","), uid]);

  // ── Player info ───────────────────────────────────────────────────────────────
  const firstMatch = matches[0] ?? null;
  const isDoublesMatch = !!firstMatch?.isDoubles;
  // Doubles-only: formation/serve-direction/return-hand breakdown, same as
  // GeneralReport.jsx's "Doubles Analysis" section.
  const doublesAnalysisData = useMemo(
    () => (isDoublesMatch ? computeDoublesAnalysis(matches, playerGuid) : null),
    [isDoublesMatch, matches, playerGuid],
  );
  const focusIsP1 = isDoublesMatch ? isFocusTeamA(firstMatch, playerGuid) : (!playerGuid || firstMatch?.player1Guid === playerGuid);
  const focusTeam = isDoublesMatch ? (focusIsP1 ? firstMatch?.teamA : firstMatch?.teamB) : null;
  const oppTeam = isDoublesMatch ? (focusIsP1 ? firstMatch?.teamB : firstMatch?.teamA) : null;
  const focusPlayer = firstMatch ? (isDoublesMatch ? null : (focusIsP1 ? firstMatch.player1 : firstMatch.player2)) : null;
  const playerName = isDoublesMatch
    ? (focusTeam?.name ?? "Team")
    : (focusPlayer ? `${focusPlayer.firstName ?? ""} ${focusPlayer.lastName ?? ""}`.trim() : "Player");
  const focusP1Name = focusTeam?.player1 ? `${focusTeam.player1.firstName ?? ""} ${focusTeam.player1.lastName ?? ""}`.trim() : "Player 1";
  const focusP2Name = focusTeam?.player2 ? `${focusTeam.player2.firstName ?? ""} ${focusTeam.player2.lastName ?? ""}`.trim() : "Player 2";
  const opponentLabel =
    matches.length === 1
      ? (() => {
          if (isDoublesMatch) return oppTeam?.name ?? "Opponents";
          const op = focusIsP1 ? firstMatch?.player2 : firstMatch?.player1;
          return op ? `${op.firstName ?? ""} ${op.lastName ?? ""}`.trim() : "Opponent";
        })()
      : `${matches.length} opponents`;

  // ── Settings handlers ─────────────────────────────────────────────────────────
  const openSettings = () => {
    setOpenaiKeyDraft(openaiKey);
    setAnthropicKeyDraft(anthropicKey);
    setModelDraft(model);
    setShowSettings(true);
  };
  const saveSettings = () => {
    const trimmedOpenai = openaiKeyDraft.trim();
    const trimmedAnthropic = anthropicKeyDraft.trim();
    setOpenaiKey(trimmedOpenai);
    setAnthropicKey(trimmedAnthropic);
    setModel(modelDraft);
    localStorage.setItem(OPENAI_KEY_LS, trimmedOpenai);
    localStorage.setItem(ANTHROPIC_KEY_LS, trimmedAnthropic);
    localStorage.setItem(OPENAI_MODEL_LS, modelDraft);
    setShowSettings(false);
  };

  // ── Fetch models available to the current API key ─────────────────────────────
  const fetchModels = async (prov, key) => {
    if (!key) return;
    setLoadingModels(true);
    setModelsError("");
    try {
      const res = await fetch(
        prov === "anthropic"
          ? "https://api.anthropic.com/v1/models?limit=1000"
          : "https://api.openai.com/v1/models",
        {
          headers:
            prov === "anthropic"
              ? {
                  "x-api-key": key,
                  "anthropic-version": "2023-06-01",
                  "anthropic-dangerous-direct-browser-access": "true",
                }
              : { Authorization: `Bearer ${key}` },
        }
      );
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(
          b?.error?.message ?? `Failed to load models (${res.status})`
        );
      }
      const body = await res.json();
      const list = (body.data ?? [])
        .map((m) => ({ id: m.id, label: m.display_name ?? m.id, provider: prov }))
        .filter((m) => prov === "anthropic" || /^(gpt-|o1|o3|chatgpt-)/.test(m.id))
        .sort((a, b) => b.id.localeCompare(a.id));
      setFetchedModels((prev) => ({ ...(prev ?? {}), [prov]: list }));
      if (
        providerOf(modelDraft) === prov &&
        list.length &&
        !list.some((m) => m.id === modelDraft)
      ) {
        setModelDraft(list[0].id);
      }
    } catch (err) {
      setModelsError(describeError(err, prov === "anthropic" ? "Anthropic" : "OpenAI"));
    } finally {
      setLoadingModels(false);
    }
  };

  const modelsFor = (prov) =>
    fetchedModels?.[prov]?.length
      ? fetchedModels[prov]
      : MODELS.filter((m) => m.provider === prov);

  // Auto-load models when the settings modal opens / provider changes
  useEffect(() => {
    if (!showSettings) return;
    const prov = providerOf(modelDraft);
    const key = prov === "anthropic" ? anthropicKeyDraft : openaiKeyDraft;
    if (key) fetchModels(prov, key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSettings, providerOf(modelDraft)]);

  // ── Generate report ───────────────────────────────────────────────────────────
  const generate = async () => {
    if (!apiKey) { openSettings(); return; }
    if (!mergedStatsA || !mergedStatsB) return;

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setReport("");
    setGenError("");
    setGenerating(true);

    const statsText =
      stream === "weakestlink" && mergedFocusP1Stats && mergedFocusP2Stats
        ? buildWeakestLinkStatsText(mergedStatsA, mergedStatsB, mergedFocusP1Stats, focusP1Name, mergedFocusP2Stats, focusP2Name, playerName, opponentLabel)
        : buildStatsText(mergedStatsA, mergedStatsB, playerName, opponentLabel);
    const systemPrompt =
      stream === "opposition" ? OPPOSITION_PROMPT : stream === "weakestlink" ? DOUBLES_WEAKEST_LINK_PROMPT : SYSTEM_PROMPT;
    const userPrompt =
      stream === "opposition"
        ? `Write an opposition game plan based on the following match statistics for the player you will be facing:\n\n${statsText}`
        : stream === "weakestlink"
          ? `Identify the team's weakest link based on the following combined and per-individual match statistics:\n\n${statsText}`
          : `Write a coaching report for the following match statistics:\n\n${statsText}`;

    const isAnthropic = provider === "anthropic";

    try {
      const res = await fetch(
        isAnthropic
          ? "https://api.anthropic.com/v1/messages"
          : "https://api.openai.com/v1/chat/completions",
        {
          method: "POST",
          headers: isAnthropic
            ? {
                "Content-Type": "application/json",
                "x-api-key": apiKey,
                "anthropic-version": "2023-06-01",
                "anthropic-dangerous-direct-browser-access": "true",
              }
            : {
                "Content-Type": "application/json",
                Authorization: `Bearer ${apiKey}`,
              },
          body: JSON.stringify(
            isAnthropic
              ? {
                  model,
                  max_tokens: 8192,
                  stream: true,
                  system: systemPrompt,
                  messages: [{ role: "user", content: userPrompt }],
                }
              : {
                  model,
                  stream: true,
                  messages: [
                    { role: "system", content: systemPrompt },
                    { role: "user", content: userPrompt },
                  ],
                }
          ),
          signal: ctrl.signal,
        }
      );

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        const apiMsg = errBody?.error?.message ?? errBody?.message;
        const apiType = errBody?.error?.type;
        const providerName = isAnthropic ? "Anthropic" : "OpenAI";
        throw new Error(
          apiMsg
            ? `${providerName} error (${res.status}${apiType ? ` ${apiType}` : ""}): ${apiMsg}`
            : `${providerName} API error ${res.status}`
        );
      }

      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";

      const handleData = (data) => {
        if (!data || data === "[DONE]") return;
        let parsed;
        try {
          parsed = JSON.parse(data);
        } catch {
          return; // skip malformed chunks
        }
        if (parsed.type === "error") {
          throw new Error(
            parsed.error?.message ?? "Streaming error from provider"
          );
        }
        const delta = isAnthropic
          ? parsed.type === "content_block_delta"
            ? parsed.delta?.text ?? ""
            : ""
          : parsed.choices?.[0]?.delta?.content ?? "";
        if (delta) setReport((prev) => prev + delta);
      };

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() ?? "";
        for (const line of lines) {
          const trimmed = line.trim();
          if (trimmed.startsWith("data:")) handleData(trimmed.slice(5).trim());
        }
      }
      // Flush any remaining decoded bytes and the last buffered line
      buf += dec.decode();
      const tail = buf.trim();
      if (tail.startsWith("data:")) handleData(tail.slice(5).trim());
    } catch (err) {
      if (err.name !== "AbortError")
        setGenError(describeError(err, isAnthropic ? "Anthropic" : "OpenAI"));
    } finally {
      setGenerating(false);
    }
  };

  const copyReport = () => {
    if (report) navigator.clipboard.writeText(report).catch(() => {});
  };

  const savePdf = async () => {
    if (!report) return;
    setSavingPdf(true);
    try {
      const { default: jsPDF } = await import("jspdf");

      const MARGIN = 15;
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const maxX = pageW - MARGIN;
      const contentW = pageW - MARGIN * 2;
      let y = MARGIN;

      const checkPage = (needed = 6) => {
        if (y + needed > pageH - MARGIN) {
          pdf.addPage();
          y = MARGIN;
        }
      };

      const writeLine = (text, opts = {}) => {
        const { size = 10, bold = false, color = [30, 30, 30], indent = 0 } = opts;
        pdf.setFontSize(size);
        pdf.setFont("helvetica", bold ? "bold" : "normal");
        pdf.setTextColor(...color);
        const lines = pdf.splitTextToSize(text, contentW - indent);
        lines.forEach((ln) => {
          checkPage(size * 0.35 + 1);
          pdf.text(ln, MARGIN + indent, y);
          y += size * 0.35 + 1;
        });
      };

      // ── Cover / title ──────────────────────────────────────────────────────
      pdf.setFillColor(30, 100, 200);
      pdf.rect(0, 0, pageW, 28, "F");
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(255, 255, 255);
      const titleLabel = stream === "opposition" ? "Opposition Game Plan" : stream === "weakestlink" ? "Weakest Link Analysis" : "Self Assessment Report";
      pdf.text(titleLabel, MARGIN, 12);
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      pdf.text(playerName, MARGIN, 19);
      pdf.text(`vs ${opponentLabel}  ·  ${matches.length} match${matches.length !== 1 ? "es" : ""}`, MARGIN, 24);
      pdf.setTextColor(30, 30, 30);
      y = 36;

      // ── Report body (parse markdown) ───────────────────────────────────────
      const lines = report.split("\n");
      for (let li = 0; li < lines.length; li++) {
        const line = lines[li];
        if (line.trim().startsWith("|") && isTableSeparatorRow(lines[li + 1] ?? "")) {
          const header = parseTableRow(line);
          const bodyRows = [];
          let j = li + 2;
          while (j < lines.length && lines[j].trim().startsWith("|")) {
            bodyRows.push(parseTableRow(lines[j]));
            j++;
          }
          const nCols = header.length;
          const colW = contentW / nCols;
          const clean = (s) => s.replace(/\*\*([^*]+)\*\*/g, "$1");
          const drawRow = (cells, bold, color) => {
            checkPage(5);
            pdf.setFont("helvetica", bold ? "bold" : "normal");
            pdf.setTextColor(...color);
            cells.forEach((c, ci) => {
              const x = MARGIN + ci * colW;
              pdf.text(clean(c), ci === 0 ? x : x + colW, y, ci === 0 ? {} : { align: "right" });
            });
            y += 4.5;
          };

          y += 3;
          checkPage(10);
          pdf.setFontSize(8.5);
          drawRow(header, true, [30, 100, 200]);
          pdf.setDrawColor(180, 180, 180);
          pdf.setLineWidth(0.2);
          pdf.line(MARGIN, y - 3, maxX, y - 3);
          pdf.setFontSize(9);
          for (const r of bodyRows) drawRow(r, false, [50, 50, 50]);
          y += 1;
          pdf.setTextColor(30, 30, 30);
          li = j - 1;
          continue;
        }
        if (line.startsWith("## ")) {
          y += 3;
          checkPage(12);
          // Section heading with underline
          pdf.setFontSize(12);
          pdf.setFont("helvetica", "bold");
          pdf.setTextColor(30, 100, 200);
          const heading = line.slice(3);
          pdf.text(heading, MARGIN, y);
          y += 1.5;
          pdf.setDrawColor(30, 100, 200);
          pdf.setLineWidth(0.3);
          pdf.line(MARGIN, y, maxX, y);
          y += 4;
          pdf.setTextColor(30, 30, 30);
        } else if (line.startsWith("### ")) {
          y += 2;
          writeLine(line.slice(4), { size: 10.5, bold: true });
          y += 1;
        } else if (line.startsWith("- ") || line.startsWith("* ")) {
          const text = line.slice(2).replace(/\*\*([^*]+)\*\*/g, "$1");
          checkPage(6);
          pdf.setFontSize(10);
          pdf.setFont("helvetica", "normal");
          pdf.setTextColor(30, 30, 30);
          pdf.text("•", MARGIN + 1, y);
          const wrapped = pdf.splitTextToSize(text, contentW - 7);
          for (let wi = 0; wi < wrapped.length; wi++) {
            if (wi > 0) checkPage(5);
            pdf.text(wrapped[wi], MARGIN + 6, y);
            y += 4.5;
          }
        } else if (!line.trim()) {
          y += 2;
        } else {
          const text = line.replace(/\*\*([^*]+)\*\*/g, "$1");
          writeLine(text, { size: 10 });
          y += 0.5;
        }
      }

      // ── Stats section ──────────────────────────────────────────────────────
      if (statRows.length > 0) {
        y += 6;
        checkPage(16);

        // Stats heading
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(30, 100, 200);
        pdf.text("Match Statistics", MARGIN, y);
        y += 1.5;
        pdf.setDrawColor(30, 100, 200);
        pdf.setLineWidth(0.3);
        pdf.line(MARGIN, y, maxX, y);
        y += 5;

        // Column header
        const col1 = MARGIN;
        const col2 = pageW - MARGIN - 40;
        const col3 = pageW - MARGIN - 15;
        pdf.setFontSize(8.5);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(100, 100, 100);
        pdf.text("Stat", col1, y);
        pdf.text(playerName.length > 18 ? playerName.slice(0, 16) + "…" : playerName, col2, y, { align: "right" });
        pdf.text(opponentLabel.length > 14 ? opponentLabel.slice(0, 12) + "…" : opponentLabel, col3, y, { align: "right" });
        y += 3;
        pdf.setDrawColor(180, 180, 180);
        pdf.setLineWidth(0.2);
        pdf.line(MARGIN, y, maxX, y);
        y += 3.5;

        for (const row of statRows) {
          if (row.section) {
            checkPage(8);
            y += 1;
            pdf.setFontSize(8);
            pdf.setFont("helvetica", "bold");
            pdf.setTextColor(120, 120, 120);
            pdf.text(row.section.toUpperCase(), col1, y);
            y += 4;
          } else {
            checkPage(5);
            pdf.setFontSize(9);
            pdf.setFont("helvetica", "normal");
            pdf.setTextColor(50, 50, 50);
            pdf.text(row.label, col1, y);
            pdf.setFont("helvetica", "bold");
            pdf.setTextColor(30, 100, 200);
            pdf.text(String(row.v1), col2, y, { align: "right" });
            pdf.setFont("helvetica", "normal");
            pdf.setTextColor(100, 100, 100);
            pdf.text(String(row.v2), col3, y, { align: "right" });
            y += 4.5;
          }
        }
      }

      // ── Doubles Analysis section ────────────────────────────────────────────
      const doublesAnalysisRows = isDoublesMatch ? buildDoublesAnalysisRows(doublesAnalysisData) : [];
      if (doublesAnalysisRows.length > 0) {
        y += 6;
        checkPage(16);

        pdf.setFontSize(12);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(30, 100, 200);
        pdf.text("Doubles Analysis", MARGIN, y);
        y += 1.5;
        pdf.setDrawColor(30, 100, 200);
        pdf.setLineWidth(0.3);
        pdf.line(MARGIN, y, maxX, y);
        y += 5;

        const dCol1 = MARGIN;
        const dCol2 = pageW - MARGIN - 40;
        const dCol3 = pageW - MARGIN - 15;
        pdf.setFontSize(8.5);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(100, 100, 100);
        pdf.text("Formation / Zone", dCol1, y);
        pdf.text(playerName.length > 18 ? playerName.slice(0, 16) + "…" : playerName, dCol2, y, { align: "right" });
        pdf.text(opponentLabel.length > 14 ? opponentLabel.slice(0, 12) + "…" : opponentLabel, dCol3, y, { align: "right" });
        y += 3;
        pdf.setDrawColor(180, 180, 180);
        pdf.setLineWidth(0.2);
        pdf.line(MARGIN, y, maxX, y);
        y += 3.5;

        for (const row of doublesAnalysisRows) {
          if (row.section) {
            checkPage(8);
            y += 1;
            pdf.setFontSize(8);
            pdf.setFont("helvetica", "bold");
            pdf.setTextColor(120, 120, 120);
            pdf.text(row.section.toUpperCase(), dCol1, y);
            y += 4;
          } else if (row.group) {
            checkPage(5);
            pdf.setFontSize(8.5);
            pdf.setFont("helvetica", "bold");
            pdf.setTextColor(80, 80, 80);
            pdf.text(row.group, dCol1, y);
            y += 4;
          } else {
            checkPage(5);
            pdf.setFontSize(8.5);
            pdf.setFont("helvetica", "normal");
            pdf.setTextColor(50, 50, 50);
            pdf.text(row.label, dCol1 + 3, y);
            pdf.setFont("helvetica", "bold");
            pdf.setTextColor(30, 100, 200);
            pdf.text(String(row.v1), dCol2, y, { align: "right" });
            pdf.setFont("helvetica", "normal");
            pdf.setTextColor(100, 100, 100);
            pdf.text(String(row.v2), dCol3, y, { align: "right" });
            y += 4;
          }
        }
      }

      // ── Footer on each page ────────────────────────────────────────────────
      const totalPages = pdf.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(7.5);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(160, 160, 160);
        pdf.text(
          `TennisStats Pro  ·  ${new Date().toLocaleDateString()}  ·  Page ${i} of ${totalPages}`,
          pageW / 2,
          pageH - 6,
          { align: "center" }
        );
      }

      const streamLabel = stream === "opposition" ? "GamePlan" : stream === "weakestlink" ? "WeakestLink" : "Report";
      const safeName = playerName.replace(/[^a-z0-9]/gi, "_");
      pdf.save(`AI_${streamLabel}_${safeName}.pdf`);
    } catch (err) {
      console.error("PDF export failed", err);
    } finally {
      setSavingPdf(false);
    }
  };

  // ── Stat summary rows (same metrics as StatsPage) ─────────────────────────────
  const statRows =
    mergedStatsA && mergedStatsB
      ? [
          { section: "Points" },
          { label: "Pressure Points Won", v1: mergedStatsA.totalPressurePointsWon, v2: mergedStatsB.totalPressurePointsWon },
          { label: "Winners", v1: mergedStatsA.totalWinners, v2: mergedStatsB.totalWinners },
          { label: "Unforced Errors", v1: mergedStatsA.forehandUnforcedErrors + mergedStatsA.backhandUnforcedErrors, v2: mergedStatsB.forehandUnforcedErrors + mergedStatsB.backhandUnforcedErrors },
          { label: "Forced Errors", v1: mergedStatsA.forehandForcingErrors + mergedStatsA.backhandForcingErrors, v2: mergedStatsB.forehandForcingErrors + mergedStatsB.backhandForcingErrors },
          { section: "Serving" },
          { label: "Aces + Serve Winners", v1: mergedStatsA.aces + mergedStatsA.serveWinners, v2: mergedStatsB.aces + mergedStatsB.serveWinners },
          { label: "Double Faults", v1: mergedStatsA.doublefaults, v2: mergedStatsB.doublefaults },
          { label: "1st Serve %", v1: pct(mergedStatsA.firstServeIns, mergedStatsA.firstServes), v2: pct(mergedStatsB.firstServeIns, mergedStatsB.firstServes) },
          { label: "1st Serve Points Won", v1: pct(mergedStatsA.firstServicePointsWon, mergedStatsA.firstServeIns), v2: pct(mergedStatsB.firstServicePointsWon, mergedStatsB.firstServeIns) },
          { label: "2nd Serve Points Won", v1: pct(mergedStatsA.secondServicePointsWon, mergedStatsA.secondServeIns), v2: pct(mergedStatsB.secondServicePointsWon, mergedStatsB.secondServeIns) },
          { section: "Returning" },
          { label: "1st Serve Return Won", v1: pct(mergedStatsA.pointsWonOnReturnOffFirstServes, mergedStatsA.returnsFirstServe), v2: pct(mergedStatsB.pointsWonOnReturnOffFirstServes, mergedStatsB.returnsFirstServe) },
          { label: "2nd Serve Return Won", v1: pct(mergedStatsA.pointsWonOnReturnOffSecondServes, mergedStatsA.returnsSecondServe), v2: pct(mergedStatsB.pointsWonOnReturnOffSecondServes, mergedStatsB.returnsSecondServe) },
          { section: "Break Points" },
          { label: "Break Points Won", v1: `${mergedStatsA.breakPointsConverted}/${mergedStatsA.breakPoints}`, v2: `${mergedStatsB.breakPointsConverted}/${mergedStatsB.breakPoints}` },
          { section: "Rally Lengths" },
          { label: "Short Rallies (0-4) Won", v1: mergedStatsA.totalShortRalliesWon, v2: mergedStatsB.totalShortRalliesWon },
          { label: "Medium Rallies Won", v1: mergedStatsA.totalMediumRalliesWon, v2: mergedStatsB.totalMediumRalliesWon },
          { label: "Long Rallies Won", v1: mergedStatsA.totalLongRalliesWon, v2: mergedStatsB.totalLongRalliesWon },
          { section: "Positioning" },
          { label: "Shots Within 2m Baseline", v1: mergedStatsA.totalShotsWithin2mOfBaseLine, v2: mergedStatsB.totalShotsWithin2mOfBaseLine },
        ]
      : [];

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col" style={{ height: "100dvh" }}>

      {/* ── Header ── */}
      <div className="shrink-0 flex items-center gap-3 px-4 py-3 border-b border-base-300 bg-base-100">
        <button className="btn btn-ghost btn-sm btn-square" onClick={() => navigate(-1)} title="Back">
          <ArrowLeftIcon className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2 flex-1 min-w-0">
          {focusPlayer?.photoUrl ? (
            <img src={focusPlayer.photoUrl} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-primary text-primary-content flex items-center justify-center font-bold shrink-0 text-sm">
              {(focusPlayer?.firstName?.[0] ?? "?").toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <p className="font-bold text-sm truncate flex items-center gap-1.5">
              <SparklesIcon className="w-3.5 h-3.5 text-primary shrink-0" />
              {playerName} — {stream === "opposition" ? "Game Plan" : stream === "weakestlink" ? "Weakest Link" : "Self Assessment"}
            </p>
            <p className="text-xs text-base-content/50 truncate">
              vs {opponentLabel}
              {matches.length > 0 && ` · ${matches.length} match${matches.length !== 1 ? "es" : ""}`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {report && (
            <button className="btn btn-ghost btn-sm" onClick={copyReport} title="Copy report">
              <ClipboardDocumentIcon className="w-4 h-4" />
            </button>
          )}
          {report && !generating && (
            <button className="btn btn-ghost btn-sm" onClick={savePdf} title="Save as PDF" disabled={savingPdf}>
              {savingPdf ? (
                <span className="loading loading-spinner loading-xs" />
              ) : (
                <ArrowDownTrayIcon className="w-4 h-4" />
              )}
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={openSettings} title="AI settings">
            <Cog6ToothIcon className="w-4 h-4" />
          </button>
          <button
            className={`btn btn-sm gap-1 ${generating ? "btn-error" : "btn-primary"}`}
            disabled={!mergedStatsA || loadingStats}
            onClick={generating ? () => abortRef.current?.abort() : generate}
          >
            {generating ? (
              <><span className="loading loading-spinner loading-xs" /> Stop</>
            ) : (
              <><SparklesIcon className="w-4 h-4" />{report ? "Regenerate" : stream === "opposition" ? "Generate Game Plan" : stream === "weakestlink" ? "Find Weakest Link" : "Generate Report"}</>
            )}
          </button>
        </div>
      </div>

      {/* ── Stream tabs ── */}
      <div className="shrink-0 flex border-b border-base-300 bg-base-100 px-4">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            stream === "self" ? "border-primary text-primary" : "border-transparent text-base-content/50 hover:text-base-content"
          }`}
          onClick={() => setStream("self")}
        >
          Self Assessment
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            stream === "opposition" ? "border-primary text-primary" : "border-transparent text-base-content/50 hover:text-base-content"
          }`}
          onClick={() => setStream("opposition")}
        >
          Opposition Game Plan
        </button>
        {isDoublesMatch && (
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              stream === "weakestlink" ? "border-primary text-primary" : "border-transparent text-base-content/50 hover:text-base-content"
            }`}
            onClick={() => setStream("weakestlink")}
          >
            Weakest Link
          </button>
        )}
      </div>

      {/* ── Body ── */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto w-full px-4 py-5 flex flex-col gap-4">

          {/* No API key */}
          {!apiKey && !generating && !report && !loadingStats && (
            <div className="alert alert-info text-sm gap-3">
              <SparklesIcon className="w-5 h-5 shrink-0" />
              <span>
                An OpenAI API key is required to generate reports.{" "}
                <button className="link font-semibold" onClick={openSettings}>
                  Configure API key →
                </button>
              </span>
            </div>
          )}

          {/* Loading stats */}
          {loadingStats && (
            <div className="flex items-center gap-2 text-sm text-base-content/50 py-4">
              <span className="loading loading-spinner loading-sm" />
              Loading match statistics…
            </div>
          )}

          {/* Ready but no report yet */}
          {mergedStatsA && !report && !generating && !genError && apiKey && (
            <div className="flex flex-col items-center gap-3 py-10 text-base-content/30">
              <SparklesIcon className="w-10 h-10" />
              {stream === "opposition" ? (
                <p className="text-sm">Click <strong>Generate Game Plan</strong> to create an AI opposition scouting report.</p>
              ) : stream === "weakestlink" ? (
                <p className="text-sm">Click <strong>Find Weakest Link</strong> to compare both partners and identify who's holding the team back.</p>
              ) : (
                <p className="text-sm">Click <strong>Generate Report</strong> to create an AI coaching analysis.</p>
              )}
            </div>
          )}

          {/* Error */}
          {genError && (
            <div className="alert alert-error text-sm">
              <span>{genError}</span>
            </div>
          )}

          {/* Report */}
          {(report || generating) && (
            <div ref={reportRef} className="bg-base-100 rounded-box border border-base-300 px-6 py-5">
              <MarkdownReport text={report} />
              {generating && (
                <span className="inline-block w-2 h-4 bg-primary animate-pulse ml-0.5 rounded-sm align-middle" />
              )}
              <div ref={reportEndRef} />
            </div>
          )}

          {/* Stats reference collapsible */}
          {mergedStatsA && (
            <div className="border border-base-300 rounded-box overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-4 py-2.5 bg-base-200 hover:bg-base-300 transition-colors text-sm font-semibold"
                onClick={() => setShowStats((v) => !v)}
              >
                <span>
                  Stats Reference{" "}
                  <span className="font-normal text-base-content/50">
                    ({matches.length} match{matches.length !== 1 ? "es" : ""})
                  </span>
                </span>
                {showStats ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
              </button>
              {showStats && (
                <div className="overflow-x-auto">
                  <table className="table table-xs w-full">
                    <thead>
                      <tr>
                        <th className="text-left">Stat</th>
                        <th className="text-right text-primary">{playerName}</th>
                        <th className="text-right text-base-content/50">{opponentLabel}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statRows.map((row, i) =>
                        row.section ? (
                          <tr key={i}>
                            <td colSpan={3} className="pt-3 pb-0.5 text-xs font-bold uppercase tracking-wide text-base-content/40">
                              {row.section}
                            </td>
                          </tr>
                        ) : (
                          <tr key={i} className="hover">
                            <td className="text-xs">{row.label}</td>
                            <td className="text-right font-medium">{String(row.v1)}</td>
                            <td className="text-right text-base-content/50">{String(row.v2)}</td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Doubles Analysis collapsible */}
          {isDoublesMatch && doublesAnalysisData && (
            <div className="border border-base-300 rounded-box overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-4 py-2.5 bg-base-200 hover:bg-base-300 transition-colors text-sm font-semibold"
                onClick={() => setShowDoublesAnalysis((v) => !v)}
              >
                <span>Doubles Analysis</span>
                {showDoublesAnalysis ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
              </button>
              {showDoublesAnalysis && (
                <div className="px-4 py-3">
                  <DoublesAnalysisTables
                    analysis={doublesAnalysisData}
                    serveFormationMode={serveFormationMode}
                    onServeFormationModeChange={setServeFormationMode}
                    serveDirectionMode={serveDirectionMode}
                    onServeDirectionModeChange={setServeDirectionMode}
                    returnFormationMode={returnFormationMode}
                    onReturnFormationModeChange={setReturnFormationMode}
                    returnHandMode={returnHandMode}
                    onReturnHandModeChange={setReturnHandMode}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Settings modal ── */}
      {showSettings && (
        <div className="modal modal-open z-50">
          <div className="modal-box max-w-sm flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Cog6ToothIcon className="w-5 h-5" />
                AI Settings
              </h3>
              <button className="btn btn-ghost btn-sm btn-square" onClick={() => setShowSettings(false)}>
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold">Model</label>
                <button
                  type="button"
                  className="btn btn-ghost btn-xs gap-1"
                  disabled={loadingModels}
                  onClick={() =>
                    fetchModels(
                      providerOf(modelDraft),
                      providerOf(modelDraft) === "anthropic"
                        ? anthropicKeyDraft
                        : openaiKeyDraft
                    )
                  }
                  title="Reload models available to your API key"
                >
                  {loadingModels ? (
                    <span className="loading loading-spinner loading-xs" />
                  ) : (
                    "Refresh"
                  )}
                </button>
              </div>
              <select
                className="select select-bordered select-sm w-full"
                value={modelDraft}
                onChange={(e) => setModelDraft(e.target.value)}
              >
                <optgroup label="OpenAI">
                  {modelsFor("openai").map((m) => (
                    <option key={m.id} value={m.id}>{m.label}</option>
                  ))}
                </optgroup>
                <optgroup label="Anthropic (Claude)">
                  {modelsFor("anthropic").map((m) => (
                    <option key={m.id} value={m.id}>{m.label}</option>
                  ))}
                </optgroup>
              </select>
              {modelsError && (
                <p className="text-xs text-error">{modelsError}</p>
              )}
              {!modelsError && fetchedModels?.[providerOf(modelDraft)] && (
                <p className="text-xs text-base-content/50">
                  Showing models available to your API key.
                </p>
              )}
            </div>

            {providerOf(modelDraft) === "anthropic" ? (
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold">Anthropic API Key</label>
                <input
                  type="text"
                  className="input input-bordered input-sm w-full font-mono"
                  placeholder="sk-ant-…"
                  value={anthropicKeyDraft}
                  onChange={(e) => setAnthropicKeyDraft(e.target.value)}
                  autoComplete="off"
                />
                <p className="text-xs text-base-content/50">
                  Stored in your browser only, never shared.{" "}
                  <a
                    href="https://console.anthropic.com/settings/keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link"
                  >
                    Get a key →
                  </a>
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold">OpenAI API Key</label>
                <input
                  type="text"
                  className="input input-bordered input-sm w-full font-mono"
                  placeholder="sk-…"
                  value={openaiKeyDraft}
                  onChange={(e) => setOpenaiKeyDraft(e.target.value)}
                  autoComplete="off"
                />
                <p className="text-xs text-base-content/50">
                  Stored in your browser only, never shared.{" "}
                  <a
                    href="https://platform.openai.com/api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link"
                  >
                    Get a key →
                  </a>
                </p>
              </div>
            )}

            <div className="modal-action">
              <button className="btn btn-ghost btn-sm" onClick={() => setShowSettings(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={saveSettings}>Save</button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => setShowSettings(false)} />
        </div>
      )}
    </div>
  );
}

export default AIReports;
